import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PartnerizeImportDialog({ open, onOpenChange, onImported }) {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [hideImported, setHideImported] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQuery("");
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get("/admin/partnerize/campaigns");
        setCampaigns(data.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load Partnerize campaigns");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (hideImported && c.already_imported) return false;
      if (!q) return true;
      return (
        c.title?.toLowerCase().includes(q) ||
        c.campaign_id?.toLowerCase().includes(q) ||
        c.vertical_name?.toLowerCase().includes(q) ||
        c.destination_url?.toLowerCase().includes(q)
      );
    });
  }, [campaigns, query, hideImported]);

  const toggle = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const visibleUnimported = filtered.filter((c) => !c.already_imported).map((c) => c.campaign_id);
    const allSelected = visibleUnimported.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleUnimported.forEach((id) => next.delete(id));
      else visibleUnimported.forEach((id) => next.add(id));
      return next;
    });
  };

  const runImport = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one brand");
      return;
    }
    setImporting(true);
    try {
      const { data } = await API.post("/admin/partnerize/brands/import", {
        campaign_ids: Array.from(selected),
      });
      toast.success(`Imported ${data.imported} brand${data.imported === 1 ? "" : "s"} from Partnerize`);
      if (onImported) onImported();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const totalImported = campaigns.filter((c) => c.already_imported).length;
  const selectedCount = selected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="partnerize-import-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
          <DialogTitle className="font-heading text-xl font-bold text-slate-900 tracking-tight">
            Import brands from Partnerize
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            {campaigns.length} campaigns available · {totalImported} already imported
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-slate-100 space-y-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, campaign ID, vertical, URL..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-10"
              data-testid="partnerize-search-input"
              autoFocus
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <label className="flex items-center gap-2 cursor-pointer text-slate-600">
              <Checkbox
                checked={hideImported}
                onCheckedChange={(v) => setHideImported(!!v)}
                data-testid="hide-imported-checkbox"
              />
              Hide already-imported
            </label>
            <button
              className="text-[#002FA7] font-medium hover:underline"
              onClick={toggleAllVisible}
              data-testid="toggle-all-btn"
            >
              {filtered.every((c) => c.already_imported || selected.has(c.campaign_id)) ? "Clear visible" : "Select all visible"}
            </button>
          </div>
        </div>

        <ScrollArea className="max-h-[420px]">
          <div className="divide-y divide-slate-100">
            {loading ? (
              <div className="px-6 py-16 text-center text-sm text-slate-400">Loading campaigns...</div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-16 text-center text-sm text-slate-400">
                {campaigns.length === 0 ? "No Partnerize campaigns found" : "No matches — adjust your search"}
              </div>
            ) : (
              filtered.map((c) => {
                const isSel = selected.has(c.campaign_id);
                const disabled = c.already_imported;
                return (
                  <div
                    key={c.campaign_id}
                    className={`flex items-start gap-3 px-6 py-3 transition-colors ${
                      disabled ? "bg-slate-50/50" : isSel ? "bg-blue-50/60" : "hover:bg-slate-50 cursor-pointer"
                    }`}
                    onClick={() => !disabled && toggle(c.campaign_id)}
                    data-testid={`partnerize-row-${c.campaign_id}`}
                  >
                    <div className="pt-0.5">
                      {disabled ? (
                        <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                      ) : (
                        <Checkbox
                          checked={isSel}
                          onCheckedChange={() => toggle(c.campaign_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">{c.title || "—"}</span>
                        {disabled && (
                          <span className="text-[10px] font-bold tracking-wider uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                            Imported
                          </span>
                        )}
                        {c.vertical_name && (
                          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                            {c.vertical_name}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {c.destination_url || ""}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400 font-mono">
                        <span>ID: {c.campaign_id}</span>
                        <span>{c.default_commission_rate || 0}% default</span>
                        <span>{c.default_currency || ""}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500" data-testid="partnerize-selected-count">
            {selectedCount} selected
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={importing}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={runImport}
              disabled={importing || selectedCount === 0}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="partnerize-import-btn"
            >
              {importing ? "Importing..." : `Import ${selectedCount || ""} brand${selectedCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
